import { useCallback, useEffect, useState } from 'react';
import type { DataTypeFilterKeys } from '../../../../../../../../modules/audiences/AudienceForm/types.ts';
import type { SegmentFilters } from '../components/SegmentsList/types.ts';
import { type DataSourceOptions, dataSourceOptions } from '../components/types.ts';
import { debouncedHandleSearch } from './utils/debouncedHandleSearch.ts';

const DATA_TYPE_FILTER_KEYS: DataTypeFilterKeys[] = [
  'past_browsing_interests',
  'lookalike',
  'lifestyles',
  'attitudes',
  'location_visits',
  'shopping',
  'advanced_demographics',
  'marketplace',
];

export type UseSegmentFiltersProps = {
  hideDataTypeFilter?: boolean;
  hideDataProviderFilter?: boolean;
  hideReachFilter?: boolean;
};

export const useSegmentFilters = ({
  hideDataTypeFilter = false,
  hideDataProviderFilter = false,
  hideReachFilter = false,
}: UseSegmentFiltersProps = {}) => {
  const [showMySegmentsOnly, setShowMySegmentsOnly] = useState(false);
  const [dataSourceFilter, setDataSourceFilter] = useState<DataSourceOptions>(dataSourceOptions.All);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [filters, setFilters] = useState<SegmentFilters>({});

  const applyHideFilters = useCallback(
    (value: SegmentFilters): SegmentFilters => {
      const result = { ...value };

      // Apply hide-based filtering synchronously
      if (hideDataTypeFilter) {
        DATA_TYPE_FILTER_KEYS.forEach(key => {
          delete result[key];
        });
      }

      if (hideDataProviderFilter) {
        delete result.dataProvider;
      }

      if (hideReachFilter) {
        delete result.reach;
      }

      // Remove DataType filters when data source would exclude them
      const datasourceHidesDataType =
        dataSourceFilter === dataSourceOptions.MyData || dataSourceFilter === dataSourceOptions.oxs3pData;
      if (datasourceHidesDataType) {
        DATA_TYPE_FILTER_KEYS.forEach(key => {
          delete result[key];
        });
      }

      return result;
    },
    [hideDataTypeFilter, hideDataProviderFilter, hideReachFilter, dataSourceFilter]
  );

  useEffect(() => {
    setFilters(previousFilters => applyHideFilters(previousFilters));
  }, [applyHideFilters]);

  const onFiltersChange = useCallback(
    (value: SegmentFilters) => {
      setFilters(applyHideFilters(value));
    },
    [applyHideFilters]
  );

  const handleSearchPhraseChange = useCallback(
    (value: string) => {
      setSearchPhrase(value);
      debouncedHandleSearch(value, filters, onFiltersChange);
    },
    [filters, onFiltersChange]
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, []);

  // Remove DataType filters when data source filter changes to MyData or oxs3pData
  useEffect(() => {
    const shouldHideDataTypeFilter =
      dataSourceFilter === dataSourceOptions.MyData || dataSourceFilter === dataSourceOptions.oxs3pData;

    if (shouldHideDataTypeFilter) {
      setFilters(prevFilters => {
        const hasDataTypeFilters = DATA_TYPE_FILTER_KEYS.some(key => prevFilters[key] !== undefined);

        if (!hasDataTypeFilters) {
          return prevFilters;
        }

        const newFilters = { ...prevFilters };
        DATA_TYPE_FILTER_KEYS.forEach(key => {
          delete newFilters[key];
        });

        return newFilters;
      });
    }
  }, [dataSourceFilter]);

  return {
    showMySegmentsOnly,
    setShowMySegmentsOnly,
    dataSourceFilter,
    setDataSourceFilter,
    searchPhrase,
    setSearchPhrase: handleSearchPhraseChange,
    filters,
    onFiltersChange,
  };
};
